
import "../styles/globals.css";
import { ToastProvider, useToast } from '../components/ToastContext';
// Below code is commented to use flagsmith at later moment if required
// import { FlagsmithProvider } from "flagsmith/react";
// import flagsmith from "flagsmith/isomorphic";
import { getLayout } from "templates/Layout/LayoutConfiguration";
import { SessionContext } from "../components/SessionContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Head from 'next/head';
import Script from 'next/script';
import { getCompanyId, setCompanyId } from 'services/identity.service';
import CustomToast from "molecules/CustomToast";
import ErrorBoundary from "molecules/ErrorBoundaries/ErrorBoundary";
import { useEffect, useRef } from "react";
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';
import {
  initializePostHog,
  isPostHogDisabled,
  getUserData,
  shouldTrackUser,
  identifyUser,
  updateCompanyGroup,
} from 'lib/analytics/posthog-helper';

// Create a stable QueryClient instance
const makeQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
        refetchOnWindowFocus: false,
      },
    },
  });
};

let browserQueryClient;

const getQueryClient = () => {
  if (typeof window === 'undefined') {
    // For SSR, always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
};

// function MyApp({ Component, pageProps, flagsmithState, router, query }) {
function MyApp({ Component, pageProps, router, query }) {
  const queryClient = getQueryClient();
  const posthogInitialized = useRef(false);

  // Initialize PostHog only in production for users who should be tracked
  useEffect(() => {
    if (isPostHogDisabled()) return;

    if (typeof window === 'undefined' || !process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN) {
      console.error("PostHog project token isn't set");
      return;
    }

    const userData = getUserData(pageProps);

    // Don't initialize if no user data or user shouldn't be tracked
    if (!userData || !shouldTrackUser(userData)) {
      return;
    }

    // Initialize only once
    if (!posthogInitialized?.current) {
      const success = initializePostHog();
      if (success) {
        posthogInitialized.current = true;
      }
    }
  }, [pageProps?.currentUserDetails, pageProps?.candidate]);

  // Identify user and set up groups in PostHog (only if initialized)
  useEffect(() => {
    if (!posthogInitialized?.current) return;

    const userData = getUserData(pageProps);
    if (!userData) return;

    identifyUser(userData, pageProps);
  }, [pageProps?.currentUserDetails, pageProps?.candidate, pageProps?.company]);

  // Update PostHog group when company switches (only for admin users)
  useEffect(() => {
    if (!posthogInitialized?.current) return;

    const userData = getUserData(pageProps);
    if (!userData || userData?.type !== 'admin') return;

    const companySlug = query?.company;
    const companyId = getCompanyId();
    
    const updatedCompany = updateCompanyGroup(userData, companySlug, companyId);
    if (updatedCompany) {
      setCompanyId(updatedCompany?.id);
    }
  }, [pageProps?.currentUserDetails?.company, query?.company]);
  const Layout = getLayout(router.pathname);
  
  return (
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <SessionContext.Provider value={pageProps?.currentUserDetails}>
          <ToastProvider>
            {/* Below code is commented to use flagsmith at later moment if required */}
            {/* <FlagsmithProvider flagsmith={flagsmith} serverState={flagsmithState}> */}
            <Head>
              <link rel="icon" href="/images/favicon.ico" />
              <title>ANSR 1Connect</title>
            </Head>
            <Script
              type="text/javascript"
              strategy="afterInteractive"
              //removed for performance improvement and until clarity is required
              // dangerouslySetInnerHTML={{
              //   __html: `
              //   (function (c, l, a, r, i, t, y) {
              //     c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
              //     t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
              //     y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
              //   })(window, document, "clarity", "script", "g8yxsz28xk");
              //   `,
              // }}
            />
            <ErrorBoundary>
              <Layout>
                <Component {...pageProps} />
              </Layout>
            </ErrorBoundary>
            {/* </FlagsmithProvider> */}
            <CustomToast {...pageProps} />
          </ToastProvider>
        </SessionContext.Provider>
      </QueryClientProvider>
    </PostHogProvider>
  );
}

MyApp.getInitialProps = async ({ ctx }) => {
  // Below code is commented to use flagsmith at later moment if required
  // await flagsmith.init({
  //   environmentID: process.env.NEXT_PUBLIC_FLAGSMITH_KEY,
  // });
  // return { flagsmithState: flagsmith.getState(), query: ctx.query };
  return { query: ctx.query };
};

export default MyApp;
